import React, { useEffect, useState } from "react";
import { Card, Banner, Button, ButtonGroup, Stack, Spinner, List } from "@shopify/polaris";
import { getConnectionStatus } from "../../services/wpApi";
import useToast from "../../hooks/useToast";

const STATUS_TO_BANNER = {
  ok: "success",
  not_activated: "info",
  auth_failed: "warning",
  blocked_403: "critical",
  timeout: "critical",
  unreachable: "critical",
  not_found: "critical",
  api_unreachable: "critical",
  unknown: "critical",
};

const STATUS_TO_TITLE = {
  ok: "Real ID is connected",
  not_activated: "License not activated",
  auth_failed: "Authentication failed",
  blocked_403: "Blocked by firewall or WAF",
  timeout: "Request timed out",
  unreachable: "Site unreachable",
  not_found: "Plugin route not found",
  api_unreachable: "Real ID API unreachable",
  unknown: "Unexpected response",
};

function getBannerStatus(status) {
  return STATUS_TO_BANNER[status] || "critical";
}

function getBannerTitle(status) {
  return STATUS_TO_TITLE[status] || "Connection check";
}

function formatCheckedAt(checkedAt) {
  if (!checkedAt) return null;
  try {
    return new Date(checkedAt).toLocaleString();
  } catch (e) {
    return checkedAt;
  }
}

/**
 * Build a plain-text diagnostic block suitable for pasting into a support
 * ticket. Includes everything the support engineer needs to triage:
 * status classification, the exact URL we hit, HTTP status code, response
 * body preview, latency, timestamp.
 */
function buildDiagnosticsText(result) {
  if (!result) return "";

  const lines = [];
  lines.push("Real ID — Connection diagnostics");
  lines.push("================================");
  lines.push("");
  lines.push(
    `Status: ${result.status}` +
      (STATUS_TO_TITLE[result.status]
        ? ` (${STATUS_TO_TITLE[result.status]})`
        : "")
  );
  if (result.detail) lines.push(`Detail: ${result.detail}`);
  if (result.recommendation)
    lines.push(`Suggested fix: ${result.recommendation}`);
  lines.push("");

  const probe = result.probe || {};
  if (probe.url) lines.push(`Probed URL: ${probe.url}`);
  if (probe.httpStatus != null) lines.push(`HTTP status: ${probe.httpStatus}`);
  if (typeof result.latencyMs === "number")
    lines.push(`Latency: ${result.latencyMs}ms`);
  if (result.checkedAt) lines.push(`Checked at: ${result.checkedAt}`);
  if (typeof result.cached === "boolean")
    lines.push(`Cached: ${result.cached}`);

  if (probe.bodyPreview) {
    lines.push("");
    lines.push("Response body (preview):");
    lines.push(probe.bodyPreview);
  }

  if (probe.data && typeof probe.data === "object") {
    lines.push("");
    lines.push("Plugin reported versions:");
    Object.keys(probe.data).forEach((key) => {
      lines.push(`  ${key}: ${probe.data[key]}`);
    });
  }

  return lines.join("\n");
}

/**
 * Best-effort clipboard write. Modern API first, falls back to a hidden
 * textarea + execCommand for older browsers and non-secure contexts.
 */
async function writeToClipboard(text) {
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    window.isSecureContext
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      // fall through to legacy path
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (e) {
    return false;
  }
}

export default function ConnectionStatus() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const setToast = useToast();

  function runCheck(force) {
    setIsLoading(true);
    setError(null);

    getConnectionStatus({ force: !!force })
      .then(({ data }) => {
        setResult(data);
      })
      .catch((e) => {
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to run connection check."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function handleCopy() {
    if (!result) return;
    const text = buildDiagnosticsText(result);
    writeToClipboard(text).then((ok) => {
      setToast(ok ? "Diagnostics copied to clipboard" : "Failed to copy diagnostics");
    });
  }

  useEffect(() => {
    runCheck(false);
  }, []);

  return (
    <Card.Section title="API Connection Status">
      <p className="ri-mb-3">
        Verifies that the Real ID API can reach this site. If post-checkout
        verification status updates aren't reaching your orders, run this
        check first.
      </p>

      {isLoading && !result && (
        <Stack alignment="center" spacing="tight">
          <Spinner accessibilityLabel="Checking connection" size="small" />
          <span>Checking connection…</span>
        </Stack>
      )}

      {error && (
        <Banner status="critical" title="Could not run connection check">
          <p>{error}</p>
        </Banner>
      )}

      {result && (
        <Stack vertical spacing="tight">
          <Banner
            status={getBannerStatus(result.status)}
            title={getBannerTitle(result.status)}
          >
            {result.status === "ok" ? (
              <List type="bullet">
                <List.Item>Real ID service reachable</List.Item>
                <List.Item>Your WordPress site reachable</List.Item>
              </List>
            ) : (
              <>
                {result.detail && <p>{result.detail}</p>}
                {result.recommendation && (
                  <p style={{ marginTop: "0.5rem" }}>
                    <strong>Suggested fix:</strong> {result.recommendation}
                  </p>
                )}
              </>
            )}
          </Banner>

          {result.status !== "ok" && result.probe && result.probe.url && (
            <p style={{ fontSize: "0.85em", color: "#637381", margin: 0 }}>
              <strong>Probed:</strong> <code>{result.probe.url}</code>
              {result.probe.httpStatus != null
                ? ` → HTTP ${result.probe.httpStatus}`
                : ""}
            </p>
          )}

          {result.checkedAt && (
            <p style={{ fontSize: "0.85em", color: "#637381", margin: 0 }}>
              Last checked at {formatCheckedAt(result.checkedAt)}
              {result.cached ? " (cached)" : ""}
            </p>
          )}
        </Stack>
      )}

      <div style={{ marginTop: "1rem" }}>
        <ButtonGroup>
          <Button
            onClick={() => runCheck(true)}
            loading={isLoading}
            disabled={isLoading}
          >
            {result ? "Re-check connection" : "Retry"}
          </Button>
          <Button onClick={handleCopy} disabled={!result}>
            Copy diagnostics
          </Button>
        </ButtonGroup>
      </div>
    </Card.Section>
  );
}
